import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Reveal } from "./motion";

export function Eyebrow({ children, dark = false }) {
  return (
    <span className={`inline-flex items-center gap-2 font-mono text-[11px] font-medium uppercase tracking-[0.25em] ${dark ? "text-aqua" : "text-steel"}`}>
      <span className="h-1.5 w-1.5 bg-aqua" aria-hidden="true" />
      {children}
    </span>
  );
}

export function SectionHead({ eyebrow, title, lead, dark = false, align = "left" }) {
  return (
    <Reveal className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      {eyebrow && <Eyebrow dark={dark}>{eyebrow}</Eyebrow>}
      <h2 className={`mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.15] ${dark ? "text-white" : "text-navy"}`} data-testid="section-heading">
        {title}
      </h2>
      {lead && <p className={`mt-5 text-lg leading-relaxed ${dark ? "text-white/70" : "text-mutedslate"}`}>{lead}</p>}
    </Reveal>
  );
}

export function ContourBg({ dark = false, className = "" }) {
  return <div aria-hidden="true" className={`absolute inset-0 pointer-events-none ${dark ? "bg-contours-dark" : "bg-contours"} ${className}`} />;
}

export function Breadcrumbs({ items, dark = false }) {
  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className={`h-3 w-3 ${dark ? "text-white/40" : "text-mutedslate/60"}`} aria-hidden="true" />}
            {item.to ? (
              <Link to={item.to} className={`transition-colors ${dark ? "text-white/60 hover:text-aqua" : "text-mutedslate hover:text-steel"}`}>
                {item.label}
              </Link>
            ) : (
              <span className={dark ? "text-aqua" : "text-steel"}>{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function PrimaryCta({ to, children, dark = false, testid, className = "" }) {
  const cls = `inline-flex items-center gap-2 font-bold text-sm px-6 py-3.5 rounded-md transition-all ${
    dark
      ? "bg-aqua text-navy hover:bg-aqua-deep hover:shadow-[0_10px_28px_rgba(32,196,199,0.35)]"
      : "bg-navy text-white hover:bg-steel"
  } ${className}`;
  if (to.startsWith("#")) {
    return (
      <a href={to} data-testid={testid} className={cls}>
        {children}
        <ArrowRight className="h-4 w-4" />
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {children}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
}

export function SecondaryCta({ to, children, dark = false, testid, className = "" }) {
  const cls = `inline-flex items-center gap-2 font-bold text-sm px-6 py-3.5 rounded-md border-2 transition-all ${
    dark
      ? "border-white/30 text-white hover:border-aqua hover:text-aqua"
      : "border-navy/20 text-navy hover:border-navy hover:bg-navy hover:text-white"
  } ${className}`;
  if (to.startsWith("#")) {
    return (
      <a href={to} data-testid={testid} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testid} className={cls}>
      {children}
    </Link>
  );
}

export function CheckList({ items, dark = false, cols = 1 }) {
  return (
    <ul className={`grid gap-x-8 gap-y-3 ${cols === 2 ? "sm:grid-cols-2" : ""}`}>
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 bg-aqua ${dark ? "" : "shadow-[0_0_0_3px_rgba(32,196,199,0.15)]"}`} aria-hidden="true" />
          <span className={`text-sm leading-relaxed ${dark ? "text-white/75" : "text-mutedslate"}`}>{item}</span>
        </li>
      ))}
    </ul>
  );
}
